import { ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider as BrandThemeProvider } from "@/contexts/ThemeContext";
import { useTheme as useNextTheme, ThemeProvider as NextThemeProvider } from "next-themes";
import { PageVisibilityProvider } from "@/contexts/PageVisibilityContext";
import { AuthProviderUnified as AuthProvider } from "@/features/auth/providers/AuthProviderUnified";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ConfigProvider, Spin, theme as antdTheme } from "antd";
import ptBR from 'antd/locale/pt_BR';

// Substitui o indicador padrão (pontinhos azuis) do Ant Design pelo mesmo
// círculo usado no LoadingSpinner compartilhado, para que todo <Spin />
// da aplicação (37+ arquivos) fique visualmente consistente sem precisar
// editar cada uso individualmente.
Spin.setDefaultIndicator(
  <span
    aria-hidden
    className="animate-spin"
    style={{
      display: 'inline-block',
      width: '1em',
      height: '1em',
      borderRadius: '9999px',
      border: '0.15em solid rgba(255, 55, 55, 0.25)',
      borderTopColor: '#FF3737',
    }}
  />
);

interface AppProvidersProps {
  children: ReactNode;
}

function AntdThemeProvider({ children }: { children: ReactNode }) {
  const { theme, systemTheme } = useNextTheme();

  const isDark = theme === 'dark' || (theme === 'system' && systemTheme === 'dark');

  return (
    <ConfigProvider
      locale={ptBR}
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#FF3737',
          ...(isDark ? {} : { colorBgBase: '#ffffff' }),
        },
        cssVar: { key: 'app' },
        hashed: false,
      }}
    >
      {children}
    </ConfigProvider>
  );
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <AuthProvider>
      <NextThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <AntdThemeProvider>
          <BrandThemeProvider>
            <PageVisibilityProvider>
              <TooltipProvider>
                <ImpersonationBanner />
                {children}
                <Toaster />
                <Sonner />
              </TooltipProvider>
            </PageVisibilityProvider>
          </BrandThemeProvider>
        </AntdThemeProvider>
      </NextThemeProvider>
    </AuthProvider>
  );
}